import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, Timestamp, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faClipboardList,
  faHandHoldingDollar,
  faPlus,
  faReceipt
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import { normalizeRequisitionStatus } from './requisitionHelpers';
import {
  formatDateTime,
  formatKes,
  getDateTimeInputValue,
  getMonthKey,
  getMonthLabel,
  resolveBillPaymentPeriodKey,
  resolveRequisitionPaymentPeriodKey,
  summarizeFixedBills,
  summarizeRequisitions,
  toDateValue
} from '../../utils/financeWorkflow';

const PAYMENT_METHODS = ['Bank Transfer', 'MPESA', 'Cash', 'Card', 'Cheque', 'Other'];

function ExpensesComp() {
  const auth = getAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bills, setBills] = useState([]);
  const [billPayments, setBillPayments] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [requisitionPayments, setRequisitionPayments] = useState([]);
  const [legacyExpenses, setLegacyExpenses] = useState([]);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formState, setFormState] = useState({
    requisitionId: '',
    amount: '',
    paymentDate: getDateTimeInputValue(),
    paymentMethod: PAYMENT_METHODS[0],
    reference: '',
    notes: '',
    payee: ''
  });

  const loadPage = async () => {
    const [
      vendorSnapshot,
      branchSnapshot,
      billSnapshot,
      billPaymentSnapshot,
      requisitionSnapshot,
      requisitionPaymentSnapshot,
      expenseSnapshot
    ] = await Promise.all([
      getDocs(collection(db, 'Vendors')),
      getDocs(collection(db, 'Branches')),
      getDocs(collection(db, 'Bills')),
      getDocs(collection(db, 'BillPayments')),
      getDocs(collection(db, 'Requisitions')),
      getDocs(collection(db, 'RequisitionPayments')),
      getDocs(collection(db, 'Expenses'))
    ]);

    setVendors(vendorSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBranches(branchSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBills(billSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBillPayments(billPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRequisitions(requisitionSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRequisitionPayments(
      requisitionPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }))
    );
    setLegacyExpenses(expenseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load operations page:', error);
        swal('Error', 'Operations spending could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  const vendorsById = useMemo(() => {
    return vendors.reduce((accumulator, vendor) => {
      accumulator[vendor.id] = vendor;
      return accumulator;
    }, {});
  }, [vendors]);

  const branchMap = useMemo(() => {
    return branches.reduce((accumulator, branch) => {
      accumulator[branch.id] = branch.branchName;
      return accumulator;
    }, {});
  }, [branches]);

  const billRows = useMemo(() => {
    return summarizeFixedBills({
      bills,
      vendorsById,
      branchesById: branchMap,
      payments: billPayments,
      periodKey
    });
  }, [billPayments, bills, branchMap, periodKey, vendorsById]);

  const requisitionRows = useMemo(() => {
    return summarizeRequisitions({
      requisitions,
      branchesById: branchMap,
      payments: requisitionPayments
    }).filter((item) => {
      const normalizedStatus = normalizeRequisitionStatus(item.status);
      return normalizedStatus === 'Approved' || normalizedStatus === 'Partially Approved';
    }).sort((first, second) => second.remainingAmount - first.remainingAmount);
  }, [branchMap, requisitionPayments, requisitions]);

  const filteredRequisitionRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return requisitionRows.filter((item) => {
      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        item.itemTitle,
        item.itemDesc,
        item.requesterName,
        item.branchName
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [requisitionRows, searchTerm]);

  const selectedRequisition = useMemo(() => {
    return requisitionRows.find((item) => item.id === formState.requisitionId) || null;
  }, [formState.requisitionId, requisitionRows]);

  const operationsSummary = useMemo(() => {
    const billSpend = billPayments
      .filter((payment) => payment.billId && resolveBillPaymentPeriodKey(payment) === periodKey)
      .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const requisitionSpend = requisitionPayments
      .filter((payment) => resolveRequisitionPaymentPeriodKey(payment) === periodKey)
      .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const outstandingRequisitions = requisitionRows.reduce(
      (sum, item) => sum + item.remainingAmount,
      0
    );

    return {
      billSpend,
      requisitionSpend,
      totalSpend: billSpend + requisitionSpend,
      outstandingRequisitions
    };
  }, [billPayments, periodKey, requisitionPayments, requisitionRows]);

  const operationsLedger = useMemo(() => {
    const billItems = billPayments
      .filter((payment) => payment.billId && resolveBillPaymentPeriodKey(payment) === periodKey)
      .map((payment) => ({
        id: `bill-${payment.id}`,
        source: 'Fixed bill',
        title: payment.billTitle || 'Fixed bill payment',
        meta: payment.vendorName || payment.payee || 'Vendor',
        amount: Number(payment.amount || 0),
        date: payment.paymentDate || payment.createdAt,
        reference: payment.reference || ''
      }));

    const requisitionItems = requisitionPayments
      .filter((payment) => resolveRequisitionPaymentPeriodKey(payment) === periodKey)
      .map((payment) => ({
        id: `req-${payment.id}`,
        source: 'Requisition payout',
        title: payment.requisitionTitle || 'Requisition payment',
        meta: payment.payee || payment.requesterName || 'Staff',
        amount: Number(payment.amount || 0),
        date: payment.paymentDate || payment.createdAt,
        reference: payment.reference || ''
      }));

    const legacyItems = legacyExpenses
      .filter((expense) => getMonthKey(toDateValue(expense.expenseDate) || toDateValue(expense.timeStamp)) === periodKey)
      .map((expense) => ({
        id: `legacy-${expense.id}`,
        source: 'Legacy expense',
        title: expense.expenseTitle || expense.expenseType || 'Expense',
        meta:
          expense.campus === 'General'
            ? 'General'
            : branchMap[expense.campus] || expense.expenseDesc || 'Legacy expense',
        amount: Number(expense.expenseAmount || 0),
        date: expense.expenseDate || expense.timeStamp,
        reference: ''
      }));

    return [...billItems, ...requisitionItems, ...legacyItems].sort((first, second) => {
      const firstTime = toDateValue(first.date)?.getTime() || 0;
      const secondTime = toDateValue(second.date)?.getTime() || 0;
      return secondTime - firstTime;
    });
  }, [billPayments, branchMap, legacyExpenses, periodKey, requisitionPayments]);

  const openModal = (requisitionId = '') => {
    const defaultRequisition = requisitionRows.find((item) => item.id === requisitionId)
      || requisitionRows.find((item) => item.remainingAmount > 0)
      || null;

    setFormState({
      requisitionId: defaultRequisition?.id || '',
      amount: defaultRequisition?.remainingAmount ? String(defaultRequisition.remainingAmount) : '',
      paymentDate: getDateTimeInputValue(),
      paymentMethod: PAYMENT_METHODS[0],
      reference: '',
      notes: '',
      payee: defaultRequisition?.requesterName || ''
    });
    setModalShow(true);
  };

  const closeModal = () => {
    if (submitting) {
      return;
    }

    setModalShow(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    if (name === 'requisitionId') {
      const nextRequisition = requisitionRows.find((item) => item.id === value);
      setFormState((currentForm) => ({
        ...currentForm,
        requisitionId: value,
        amount: nextRequisition?.remainingAmount ? String(nextRequisition.remainingAmount) : '',
        payee: nextRequisition?.requesterName || currentForm.payee
      }));
      return;
    }

    setFormState((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!auth.currentUser) {
      swal('Error', 'You need to be signed in to record a requisition payment.', 'error');
      return;
    }

    if (!selectedRequisition) {
      swal('Missing requisition', 'Please choose an approved requisition first.', 'warning');
      return;
    }

    const amountValue = Number(formState.amount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      swal('Invalid amount', 'Payment amount should be greater than zero.', 'warning');
      return;
    }

    if (amountValue > selectedRequisition.remainingAmount) {
      swal(
        'Amount too high',
        `You can only pay up to ${formatKes(selectedRequisition.remainingAmount)} for this requisition.`,
        'warning'
      );
      return;
    }

    const paymentDateValue = new Date(formState.paymentDate);
    if (Number.isNaN(paymentDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid payment date.', 'warning');
      return;
    }

    setSubmitting(true);

    try {
      const paymentRef = doc(collection(db, 'RequisitionPayments'));
      await setDoc(paymentRef, {
        requisitionId: selectedRequisition.id,
        requisitionTitle: selectedRequisition.itemTitle,
        requesterId: selectedRequisition.requesterId || '',
        requesterName: selectedRequisition.requesterName || '',
        branchId: selectedRequisition.selectedCampus || '',
        branchName: selectedRequisition.branchName || '',
        payee: formState.payee.trim() || selectedRequisition.requesterName || 'Staff',
        amount: amountValue,
        paymentDate: Timestamp.fromDate(paymentDateValue),
        paymentMethod: formState.paymentMethod,
        reference: formState.reference.trim(),
        notes: formState.notes.trim(),
        paymentPeriodKey: getMonthKey(paymentDateValue),
        paymentPeriodLabel: getMonthLabel(getMonthKey(paymentDateValue)),
        expectedAmount: selectedRequisition.expectedAmount,
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser.uid,
        docId: paymentRef.id
      });

      swal('Success', 'Requisition payment recorded successfully.', 'success');
      setModalShow(false);
      await loadPage();
    } catch (error) {
      console.error('Failed to record requisition payment:', error);
      swal('Error', 'Requisition payment could not be recorded right now.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="teamsComponent opsWorkspace">
      <section className="opsHero">
        <div className="opsHeroCopy">
          <span className="opsEyebrow">Nutrition And Dietetics Academy operations</span>
          <h3>Operations spend</h3>
          <p>
            Keep operations finance focused on two things only: fixed bills and requisition
            payouts.
          </p>
        </div>
        <div className="opsHeroActions">
          <button
            type="button"
            className="opsGhostBtn opsGhostBtn--hero"
            onClick={() => navigate(`/pay-bills?period=${periodKey}`)}
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Pay fixed bill
          </button>
          <button type="button" className="opsPrimaryBtn" onClick={() => openModal()}>
            <FontAwesomeIcon icon={faPlus} /> Pay requisition
          </button>
        </div>
      </section>

      <section className="opsStatsGrid">
        <article className="opsStatCard">
          <span>Fixed bills paid</span>
          <strong>{formatKes(operationsSummary.billSpend)}</strong>
        </article>
        <article className="opsStatCard">
          <span>Requisition payouts</span>
          <strong>{formatKes(operationsSummary.requisitionSpend)}</strong>
        </article>
        <article className="opsStatCard">
          <span>{getMonthLabel(periodKey)} total spend</span>
          <strong>{formatKes(operationsSummary.totalSpend)}</strong>
        </article>
        <article className="opsStatCard">
          <span>Outstanding approved requisitions</span>
          <strong>{formatKes(operationsSummary.outstandingRequisitions)}</strong>
        </article>
      </section>

      <section className="opsLayout">
        <article className="opsPanel opsPanel--wide">
          <div className="opsToolbar">
            <div>
              <h4>Approved requisitions waiting for payout</h4>
              <p>Finance can disburse these gradually. Each payment compounds until the request is fully covered.</p>
            </div>
            <div className="opsFilters">
              <Form.Control
                type="month"
                value={periodKey}
                onChange={(event) => setPeriodKey(event.target.value)}
              />
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search requester, item, campus..."
              />
            </div>
          </div>

          {loading ? (
            <div className="opsLoadingCard">
              <Spinner animation="border" role="status" />
              <span>Loading operations workflow...</span>
            </div>
          ) : filteredRequisitionRows.length === 0 ? (
            <div className="opsEmptyState">
              <h6>No approved requisitions to pay</h6>
              <p>Approved requests will appear here once finance can start paying them out.</p>
            </div>
          ) : (
            <div className="opsTableWrap">
              <table className="opsTable">
                <thead>
                  <tr>
                    <th>Request</th>
                    <th>Campus</th>
                    <th>Requester</th>
                    <th>Amount</th>
                    <th>Paid</th>
                    <th>Remaining</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequisitionRows.map((item) => (
                    <tr key={item.id}>
                      <td data-label="Request">
                        <div className="opsDetailStack">
                          <strong>{item.itemTitle}</strong>
                          <small>{item.itemDesc || 'No description'}</small>
                        </div>
                      </td>
                      <td data-label="Campus">{item.branchName}</td>
                      <td data-label="Requester">{item.requesterName || 'Staff'}</td>
                      <td data-label="Amount">{formatKes(item.expectedAmount)}</td>
                      <td data-label="Paid">{formatKes(item.paidAmount)}</td>
                      <td data-label="Remaining">{formatKes(item.remainingAmount)}</td>
                      <td data-label="Status">
                        <span className={`opsStatusPill opsStatusPill--${item.paymentTone}`}>
                          {item.paymentStatus}
                        </span>
                      </td>
                      <td data-label="Action">
                        <button type="button" className="opsGhostBtn" onClick={() => openModal(item.id)}>
                          <FontAwesomeIcon icon={faHandHoldingDollar} /> Pay
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>

        <article className="opsPanel">
          <div className="opsSectionHeader">
            <div className="opsSectionIcon">
              <FontAwesomeIcon icon={faReceipt} />
            </div>
            <div>
              <h4>Operations ledger</h4>
              <p>{getMonthLabel(periodKey)} across fixed bills, requisition payouts, and legacy manual entries.</p>
            </div>
          </div>

          {operationsLedger.length === 0 ? (
            <div className="opsEmptyState opsEmptyState--compact">
              <h6>No operations spend recorded</h6>
              <p>Once you pay a bill or requisition, the ledger for that month will appear here.</p>
            </div>
          ) : (
            <div className="opsList">
              {operationsLedger.map((item) => (
                <div key={item.id} className="opsListItem">
                  <div>
                    <strong>{item.title}</strong>
                    <small>{item.source} • {item.meta}</small>
                    {item.reference ? <small>Ref: {item.reference}</small> : null}
                  </div>
                  <div className="opsListItemAside">
                    <strong>{formatKes(item.amount)}</strong>
                    <small>{formatDateTime(item.date)}</small>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="opsInsightCard">
            <div className="opsInsightIcon">
              <FontAwesomeIcon icon={faClipboardList} />
            </div>
            <div>
              <strong>Operations is now a cleaner workflow</strong>
              <p>
                Instead of free-form expense entries, this page now centers on the two operational
                spend paths that matter most: fixed bills and requisition payments.
              </p>
            </div>
          </div>

          {billRows.length > 0 ? (
            <div className="opsMiniSummary">
              <h6>Fixed bill snapshot</h6>
              {billRows
                .filter((item) => item.remainingAmount > 0)
                .sort((first, second) => second.remainingAmount - first.remainingAmount)
                .slice(0, 3)
                .map((item) => (
                  <div key={item.id} className="opsMiniSummaryRow">
                    <span>{item.billTitle}</span>
                    <strong>{formatKes(item.remainingAmount)}</strong>
                  </div>
                ))}
            </div>
          ) : null}
        </article>
      </section>

      <Modal show={modalShow} onHide={closeModal} centered>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Register requisition payout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Approved requisition</Form.Label>
            <Form.Select
              name="requisitionId"
              value={formState.requisitionId}
              onChange={handleFormChange}
            >
              <option value="">Select requisition</option>
              {requisitionRows.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.itemTitle} • {item.requesterName || 'Staff'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {selectedRequisition ? (
            <div className="opsInlineSummary">
              <div>
                <span>Approved amount</span>
                <strong>{formatKes(selectedRequisition.expectedAmount)}</strong>
              </div>
              <div>
                <span>Already paid</span>
                <strong>{formatKes(selectedRequisition.paidAmount)}</strong>
              </div>
              <div>
                <span>Remaining</span>
                <strong>{formatKes(selectedRequisition.remainingAmount)}</strong>
              </div>
            </div>
          ) : null}

          <Form.Group className="mb-3">
            <Form.Label>Payee</Form.Label>
            <Form.Control
              name="payee"
              value={formState.payee}
              onChange={handleFormChange}
              placeholder="Recipient name"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              name="amount"
              value={formState.amount}
              onChange={handleFormChange}
              placeholder="0"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Payment date</Form.Label>
            <Form.Control
              type="datetime-local"
              name="paymentDate"
              value={formState.paymentDate}
              onChange={handleFormChange}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Payment method</Form.Label>
            <Form.Select
              name="paymentMethod"
              value={formState.paymentMethod}
              onChange={handleFormChange}
            >
              {PAYMENT_METHODS.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Reference</Form.Label>
            <Form.Control
              name="reference"
              value={formState.reference}
              onChange={handleFormChange}
              placeholder="Transaction reference"
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formState.notes}
              onChange={handleFormChange}
              placeholder="Optional payout note"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={closeModal} disabled={submitting}>
            Close
          </Button>
          <Button variant="dark" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" /> Saving...
              </>
            ) : (
              'Record payout'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ExpensesComp;
